// Auto Clipping Phase 1: extract a video strip's audio as a compact WAV blob
// for the `audio:transcribe` backend route.
//
// Two sources, tried in order:
//   1. DecodedAudioStore — video strips already decode their audio track into
//      the store (keyed by ASSET uuid since Phase A of the audio overhaul,
//      buffer starts at media time 0). When the source is finalized this is
//      free: no second decode.
//   2. decodeAudioData fallback — decodes the asset blob's audio track through
//      an OfflineAudioContext pinned at the target sample rate. Covers strips
//      whose decoded-audio path was skipped (non-AAC codec) or not finished.
//
// Output is mono 16kHz 16-bit PCM WAV: ~32KB per second, which keeps even
// hour-long videos far below the socket buffer limit while staying more than
// good enough for speech-to-text. Transcript word times stay in SOURCE
// seconds because the extracted audio starts at media time 0.

import { getDecodedAudioStore } from './DecodedAudioStore'

export const TRANSCRIBE_SAMPLE_RATE = 16000

// Frames per DecodedAudioStore read. Reads are copies, so this bounds the
// transient memory per chunk (~8MB at 48kHz stereo) instead of copying the
// whole source buffer at once.
const STORE_READ_CHUNK_FRAMES = 1 << 20

// Downmix + linear-resample a DecodedAudioStore source into mono Float32 at
// the transcription rate. Single forward pass; each aligned chunk is read
// with one frame of overlap so interpolation never straddles two reads.
function monoFromDecodedStore(store, uuid) {
  const info = store.getInfo(uuid)
  if (!info || !info.ready || !info.totalFrames) return null
  const srcRate = info.sampleRate
  const channels = Math.max(1, info.channels)
  const totalFrames = info.totalFrames
  const ratio = srcRate / TRANSCRIBE_SAMPLE_RATE
  const outFrames = Math.max(1, Math.floor(totalFrames / ratio))
  const out = new Float32Array(outFrames)

  let chunkStart = -1
  let chunk = null
  const loadChunkFor = (frame) => {
    chunkStart = Math.floor(frame / STORE_READ_CHUNK_FRAMES) * STORE_READ_CHUNK_FRAMES
    chunk = store.readFrames(uuid, chunkStart, STORE_READ_CHUNK_FRAMES + 1)
  }
  const monoAt = (frame) => {
    if (chunk === null || frame < chunkStart || frame > chunkStart + STORE_READ_CHUNK_FRAMES) {
      loadChunkFor(frame)
    }
    const base = (frame - chunkStart) * channels
    let sum = 0
    for (let c = 0; c < channels; c++) sum += chunk[base + c] || 0
    return sum / channels
  }

  for (let j = 0; j < outFrames; j++) {
    const s = j * ratio
    const f0 = Math.min(totalFrames - 1, Math.floor(s))
    const f1 = Math.min(totalFrames - 1, f0 + 1)
    const frac = s - f0
    const a = monoAt(f0)
    const b = f1 === f0 ? a : monoAt(f1)
    out[j] = a + (b - a) * frac
  }
  return out
}

// Decode the blob's audio track at the transcription rate (the context rate
// drives decodeAudioData's resample), then downmix to mono.
async function monoFromBlobDecode(blob) {
  const arrayBuffer = await blob.arrayBuffer()
  const ctx = new OfflineAudioContext(1, 1, TRANSCRIBE_SAMPLE_RATE)
  const audioBuffer = await ctx.decodeAudioData(arrayBuffer)
  const channels = audioBuffer.numberOfChannels
  const frames = audioBuffer.length
  if (!frames) return null
  const out = new Float32Array(frames)
  for (let c = 0; c < channels; c++) {
    const data = audioBuffer.getChannelData(c)
    for (let i = 0; i < frames; i++) out[i] += data[i]
  }
  if (channels > 1) {
    for (let i = 0; i < frames; i++) out[i] /= channels
  }
  return out
}

function encodeWavPcm16Mono(samples, sampleRate) {
  const dataBytes = samples.length * 2
  const buffer = new ArrayBuffer(44 + dataBytes)
  const view = new DataView(buffer)
  const writeAscii = (offset, text) => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i))
  }
  writeAscii(0, 'RIFF')
  view.setUint32(4, 36 + dataBytes, true)
  writeAscii(8, 'WAVE')
  writeAscii(12, 'fmt ')
  view.setUint32(16, 16, true) // fmt chunk size
  view.setUint16(20, 1, true) // PCM
  view.setUint16(22, 1, true) // mono
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true) // byte rate
  view.setUint16(32, 2, true) // block align
  view.setUint16(34, 16, true) // bits per sample
  writeAscii(36, 'data')
  view.setUint32(40, dataBytes, true)
  let offset = 44
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]))
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true)
    offset += 2
  }
  return new Blob([buffer], { type: 'audio/wav' })
}

// Extract the audio of a video strip/asset as a mono 16kHz WAV.
// `sourceKey` selects the DecodedAudioStore source (fast path) — since Phase A
// of the audio overhaul the store is keyed by ASSET uuid (`stripUuid` is kept
// as a legacy alias). `blob` is the video asset blob used by the decode
// fallback. Throws on failure (e.g. the video has no decodable audio track).
export async function extractTranscriptionAudio({ sourceKey = null, stripUuid = null, blob = null, name = 'audio' } = {}) {
  let mono = null
  let source = null
  const key = sourceKey || stripUuid

  if (key) {
    try {
      const store = getDecodedAudioStore()
      if (store.isReady(key)) {
        mono = monoFromDecodedStore(store, key)
        if (mono) source = 'decoded-store'
      }
    } catch (e) {
      mono = null
    }
  }

  if (!mono && blob) {
    mono = await monoFromBlobDecode(blob)
    if (mono) source = 'decode'
  }

  if (!mono || !mono.length) {
    throw new Error('No decodable audio found for transcription')
  }

  const wav = encodeWavPcm16Mono(mono, TRANSCRIBE_SAMPLE_RATE)
  return {
    blob: wav,
    mime: 'audio/wav',
    filename: `${name}.wav`,
    durationSeconds: mono.length / TRANSCRIBE_SAMPLE_RATE,
    sampleRate: TRANSCRIBE_SAMPLE_RATE,
    source,
  }
}
